import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AdminLayout from '../layout/AdminLayout';
import {
  getReviewPermission,
  getSupportThreads,
  getSupportThread,
  replySupportThread,
  closeSupportThread,
  SupportThread,
  SupportMessage,
} from '../../services/providerReviewService';
import '../review/BotReviewPage.css';
import './SupportPage.css';

const STATUSES = ['all', 'open', 'answered', 'closed'] as const;

/**
 * Provider support inbox (2026-07-09): threads opened by providers from their
 * console. An admin reply marks the thread answered and emails the provider;
 * Close resolves it (a later provider reply reopens it).
 */
const SupportPage: React.FC = () => {
  const { t } = useTranslation();
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const [threads, setThreads] = useState<SupportThread[]>([]);
  const [statusFilter, setStatusFilter] = useState<(typeof STATUSES)[number]>('all');
  const [selected, setSelected] = useState<string | null>(null);
  const [thread, setThread] = useState<SupportThread | null>(null);
  const [messages, setMessages] = useState<SupportMessage[]>([]);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setThreads(await getSupportThreads(statusFilter));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [statusFilter]);

  useEffect(() => {
    getReviewPermission().then((ok) => {
      setAllowed(ok);
    });
  }, []);

  useEffect(() => {
    if (allowed) load();
  }, [allowed, load]);

  const openThread = async (threadId: string) => {
    setSelected(threadId);
    setNotice(null);
    setError(null);
    try {
      const r = await getSupportThread(threadId);
      setThread(r.thread);
      setMessages(r.messages);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };

  const sendReply = async () => {
    if (!selected || !reply.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await replySupportThread(selected, reply.trim());
      setReply('');
      setNotice(r.emailSent
        ? (t('support.replied', 'Reply sent — the provider was notified by email.') as string)
        : (t('support.repliedNoEmail', 'Reply sent (email notification not sent in this environment).') as string));
      await openThread(selected);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const close = async () => {
    if (!selected || busy) return;
    setBusy(true);
    try {
      await closeSupportThread(selected);
      setNotice(t('support.closed', 'Thread closed.') as string);
      await openThread(selected);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString() : '—');
  /**
   * Bot context (2026-09-12): the bot the provider picked when opening the
   * thread; threads without one fall back to the provider's bot list.
   */
  const botCell = (th: SupportThread) => {
    if (th.botName || th.botId) return <span className="support-bot">{th.botName || th.botId}</span>;
    if (th.providerBotNames && th.providerBotNames.length > 0) {
      return (
        <span className="support-bot muted" title={t('support.botOther', "Provider's bots:") as string}>
          {th.providerBotNames.join(', ')}
        </span>
      );
    }
    return <span className="support-bot muted">{t('support.botNone', 'No bot')}</span>;
  };
  const statusPill = (status: string) => (
    <span className={'status-pill support-' + status}>{t(`support.status.${status}`, status)}</span>
  );

  if (allowed === null) return <AdminLayout><div className="review-page" /></AdminLayout>;
  if (!allowed) {
    return (
      <AdminLayout>
        <div className="review-page">
          <p>{t('review.noPermission', 'You do not have permission to review bots.')}</p>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="review-page">
        <div className="review-head">
          <div>
            <h2>{t('support.title', 'Support')}</h2>
            <p className="review-sub">{t('support.subtitle', 'Support requests from providers. Replies are emailed to the provider.')}</p>
          </div>
          <div className="support-controls">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as (typeof STATUSES)[number])}>
              {STATUSES.map((s) => (
                <option key={s} value={s}>{t(`support.filter.${s}`, s)}</option>
              ))}
            </select>
            <button className="secondary" onClick={load}>{t('review.refresh', 'Refresh')}</button>
          </div>
        </div>

        {error && <div className="review-error">{error}</div>}

        <div className="support-grid">
          <div className="support-list">
            {threads.length === 0 ? (
              <p className="detail-muted">{t('support.empty', 'No support threads.')}</p>
            ) : (
              <table className="review-table support-table">
                <thead>
                  <tr>
                    <th className="col-subject">{t('support.colSubject', 'Subject')}</th>
                    <th className="col-bot">{t('support.colBot', 'Bot')}</th>
                    <th className="col-provider">{t('support.colProvider', 'Provider')}</th>
                    <th className="col-status">{t('support.colStatus', 'Status')}</th>
                    <th className="col-updated">{t('support.colUpdated', 'Updated')}</th>
                  </tr>
                </thead>
                <tbody>
                  {threads.map((th) => (
                    <tr
                      key={th.threadId}
                      className={'support-row' + (selected === th.threadId ? ' selected' : '')}
                      onClick={() => openThread(th.threadId)}
                    >
                      <td className="col-subject">
                        <div className="support-subject">{th.subject}</div>
                        {th.messageCount ? <div className="support-sub">{th.messageCount} msg{th.messageCount === 1 ? '' : 's'}</div> : null}
                      </td>
                      <td className="col-bot">{botCell(th)}</td>
                      <td className="col-provider">
                        <div className="support-provider">{th.providerName || th.providerEmail || th.providerId}</div>
                        {th.providerName && th.providerEmail && <div className="support-sub">{th.providerEmail}</div>}
                      </td>
                      <td className="col-status">{statusPill(th.status)}</td>
                      <td className="col-updated">{fmt(th.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {selected && thread && (
            <div className="support-detail detail-block">
              <div className="support-detail-head">
                <h4>{thread.subject}</h4>
                <div>
                  {statusPill(thread.status)}
                  {thread.status !== 'closed' && (
                    <button className="secondary danger-text" disabled={busy} onClick={close}>
                      {t('support.close', 'Close thread')}
                    </button>
                  )}
                </div>
              </div>
              <div className="support-detail-meta">
                <span><strong>{t('support.colBot', 'Bot')}:</strong> {botCell(thread)}</span>
                <span><strong>{t('support.colProvider', 'Provider')}:</strong> {thread.providerName || thread.providerEmail || thread.providerId}{thread.providerName && thread.providerEmail ? ` (${thread.providerEmail})` : ''}</span>
                <span>{fmt(thread.createdAt)}</span>
              </div>
              <div className="support-msgs">
                {messages.map((m, i) => (
                  <div key={i} className={'support-bubble ' + m.sender}>
                    <div className="support-bubble-meta">
                      {m.sender === 'admin'
                        ? `${t('support.admin', 'Support')}${m.by ? ` (${m.by})` : ''}`
                        : t('support.provider', 'Provider')}
                      {' · '}{fmt(m.at)}
                    </div>
                    <div className="support-bubble-body">{m.body}</div>
                  </div>
                ))}
              </div>
              <div className="preview-input">
                <textarea
                  value={reply}
                  placeholder={t('support.replyPh', 'Write a reply to the provider…') as string}
                  onChange={(e) => setReply(e.target.value)}
                  maxLength={4000}
                  disabled={busy}
                />
                <button onClick={sendReply} disabled={busy || !reply.trim()}>
                  {t('support.send', 'Send reply')}
                </button>
              </div>
              {notice && <p className="detail-muted">{notice}</p>}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default SupportPage;
